// Phase-1 + Phase-3 auth token storage (PLAN.md §9, §21.3).
// Tokens persist in localStorage so the session survives closing the tab/app —
// the Cognito refresh token (valid 30 days) then lets the SPA renew silently
// instead of forcing a re-login on every cold start. Stored values are session
// tokens only — non-PHI (PLAN.md §1266); no PHI is ever kept client-side.
//
// Phase-1 (USE_COGNITO=false): stores a single HS256 JWT under "medassist.token".
// Phase-3 (USE_COGNITO=true):  stores Cognito AccessToken, IdToken, RefreshToken
//   and expiry. Phase-1 key is kept so the logout path always works.

import { decodeJwtPayload } from "./cognito.js";

// ---- Phase-1 compat ----
const KEY = "medassist.token";

/** Phase-1: read the raw token (used by api.ts `call()` for Bearer header). */
export function getToken(): string | null {
  // Phase-3: return the ID token — it contains cognito:groups for authorization.
  // The access token does NOT include groups; the ID token does.
  return localStorage.getItem(KEY_ID) ?? localStorage.getItem(KEY_ACCESS) ?? localStorage.getItem(KEY);
}

/** Phase-1: store a raw token. */
export function setToken(token: string): void {
  localStorage.setItem(KEY, token);
}

/** Clear all auth state (Phase-1 + Phase-3). */
export function clearToken(): void {
  localStorage.removeItem(KEY);
  localStorage.removeItem(KEY_ACCESS);
  localStorage.removeItem(KEY_ID);
  localStorage.removeItem(KEY_REFRESH);
  localStorage.removeItem(KEY_EXPIRES_AT);
}

// ---- Phase-3: Cognito token storage ----
const KEY_ACCESS     = "cognitoAccessToken";
const KEY_ID         = "cognitoIdToken";
const KEY_REFRESH    = "cognitoRefreshToken";
const KEY_EXPIRES_AT = "cognitoExpiresAt"; // epoch ms

export interface CognitoTokenSet {
  accessToken:  string;
  idToken:      string;
  refreshToken: string;
  expiresAt:    number; // Date.now() ms when access token expires
}

export function setCognitoTokens(t: { accessToken: string; idToken: string; refreshToken?: string; expiresIn: number; issuedAt: number }): void {
  localStorage.setItem(KEY_ACCESS,    t.accessToken);
  localStorage.setItem(KEY_ID,        t.idToken);
  if (t.refreshToken) localStorage.setItem(KEY_REFRESH, t.refreshToken);
  localStorage.setItem(KEY_EXPIRES_AT, String(t.issuedAt + t.expiresIn * 1000));
}

export function getCognitoTokens(): CognitoTokenSet | null {
  const a = localStorage.getItem(KEY_ACCESS);
  const i = localStorage.getItem(KEY_ID);
  const r = localStorage.getItem(KEY_REFRESH);
  const e = localStorage.getItem(KEY_EXPIRES_AT);
  if (!a || !i || !r) return null;
  return { accessToken: a, idToken: i, refreshToken: r, expiresAt: Number(e) };
}

export function getIdToken(): string | null {
  return localStorage.getItem(KEY_ID);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(KEY_REFRESH);
}

export function isSignedIn(): boolean {
  // Phase-3: check Cognito access token exists and is not expired.
  const expires = Number(localStorage.getItem(KEY_EXPIRES_AT));
  if (localStorage.getItem(KEY_ACCESS) && expires && Date.now() < expires) return true;
  // Phase-1 fallback.
  return !!localStorage.getItem(KEY);
}

/** Returns the Cognito groups from the stored ID token, or [] if not available. */
export function getGroups(): string[] {
  const id = getIdToken();
  if (!id) return [];
  const payload = decodeJwtPayload(id);
  const groups  = payload["cognito:groups"];
  if (Array.isArray(groups)) return groups as string[];
  return [];
}

/** True when signed in via Cognito but not yet assigned to any group. */
export function isPendingApproval(): boolean {
  return !!localStorage.getItem(KEY_ACCESS) && getGroups().length === 0;
}

/** Read the user's email from the stored Cognito ID token. */
export function getUserEmail(): string {
  const id = getIdToken();
  if (!id) return "";
  const payload = decodeJwtPayload(id);
  return (payload["email"] as string | undefined) ?? "";
}

